/**
 * Shared fetch client for calling Hono internal API endpoints.
 *
 * - Client-side only (uses credentials: "include" for session cookies)
 * - All API layer files (lib/api/*.ts) should use these helpers
 * - No React dependencies — plain async functions
 */

import { FLOW_LANGUAGE_HEADER } from "@repo/shared";
import type { ApiErrorBody, ApiErrorCode } from "@repo/shared/types";

const API_BASE = process.env.BUN_PUBLIC_API_URL;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public requestId?: string,
    /** Parsed JSON body of the error response (or null if unparseable). Lets callers read
     * structured payloads — e.g. the 422 from POST /complete returns `{ error, requirements }`. */
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The parsed body of a failed request, read as the shared error shape. Partial
 * because the server is not trusted to have sent it (a proxy's HTML 502 parses
 * to null) — check each field's type before using it. Null for anything that
 * is not an `ApiError`.
 */
export function apiErrorBody(err: unknown): Partial<ApiErrorBody> | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  return typeof body === "object" && body !== null ? (body as Partial<ApiErrorBody>) : null;
}

/**
 * The machine-readable `code` of an API error response (e.g. `risk_matrix_gap`), if any.
 * Typed as the codes the web knows, so comparing against a renamed or mistyped
 * code fails type-checking; any other code simply never matches.
 */
export function apiErrorCode(err: unknown): ApiErrorCode | undefined {
  const code = apiErrorBody(err)?.code;
  return typeof code === "string" ? (code as ApiErrorCode) : undefined;
}

/** Generic 500 / 503 codes: their text says nothing about what failed, the caller's does. */
const GENERIC_ERROR_CODES: ReadonlySet<ApiErrorCode> = new Set(["internal_error", "unavailable"]);

/**
 * The server's error message when it sent a specific one, else `fallback` (the
 * caller's translated text — never the generic "Request failed with status N"
 * nor the error handler's generic "Internal server error"). Anything that is not
 * an `ApiError` shows `fallback` too: a network failure's `TypeError` carries the
 * browser's English ("Failed to fetch"), other errors a developer's.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const code = apiErrorCode(err);
    if (code && GENERIC_ERROR_CODES.has(code)) return fallback;
    const error = apiErrorBody(err)?.error;
    return (typeof error === "string" && error) || fallback;
  }
  return fallback;
}

/**
 * QL-17 — the flow language the advisor flow wizard is showing, sent with
 * every session request so API errors come back in it. Set by
 * `useRequestFlowLanguage` while the wizard is open, `null` otherwise.
 */
let requestFlowLanguage: string | null = null;

export function setRequestFlowLanguage(language: string | null): void {
  requestFlowLanguage = language;
}

function sessionHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    ...extra,
    "x-request-id": crypto.randomUUID(),
    ...(requestFlowLanguage ? { [FLOW_LANGUAGE_HEADER]: requestFlowLanguage } : {}),
  };
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    ...options,
    headers: sessionHeaders({ "Content-Type": "application/json" }),
  });

  if (!response.ok) {
    const requestId = response.headers.get("x-request-id") ?? undefined;
    const body = await response.json().catch(() => null);
    const message = body?.error || `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message, requestId, body);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

export function get<T>(url: string): Promise<T> {
  return request<T>(url);
}

export function post<T>(url: string, data?: unknown): Promise<T> {
  return request<T>(url, { method: "POST", body: JSON.stringify(data) });
}

export function put<T>(url: string, data?: unknown): Promise<T> {
  return request<T>(url, { method: "PUT", body: JSON.stringify(data) });
}

export function patch<T>(url: string, data?: unknown): Promise<T> {
  return request<T>(url, { method: "PATCH", body: JSON.stringify(data) });
}

export function del<T = void>(url: string): Promise<T> {
  return request<T>(url, { method: "DELETE" });
}

export async function postFormData<T>(url: string, data: FormData): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    method: "POST",
    credentials: "include",
    headers: sessionHeaders(),
    body: data,
  });

  if (!response.ok) {
    const requestId = response.headers.get("x-request-id") ?? undefined;
    const body = await response.json().catch(() => null);
    const message = body?.error || `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message, requestId, body);
  }

  return response.json();
}

// --- Bearer token variants (for public/client flow API calls) ---

async function requestWithToken<T>(url: string, token: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-request-id": crypto.randomUUID(),
    },
    ...options,
  });

  if (!response.ok) {
    const requestId = response.headers.get("x-request-id") ?? undefined;
    const body = await response.json().catch(() => null);
    const message = body?.error || `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message, requestId, body);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

export function getWithToken<T>(url: string, token: string): Promise<T> {
  return requestWithToken<T>(url, token);
}

export function postWithToken<T>(url: string, token: string, data?: unknown): Promise<T> {
  return requestWithToken<T>(url, token, { method: "POST", body: JSON.stringify(data) });
}

export function patchWithToken<T>(url: string, token: string, data?: unknown): Promise<T> {
  return requestWithToken<T>(url, token, { method: "PATCH", body: JSON.stringify(data) });
}

export function delWithToken<T = void>(url: string, token: string): Promise<T> {
  return requestWithToken<T>(url, token, { method: "DELETE" });
}
